import { createContext, useContext, useMemo, useState } from 'react';

import { Routes, topNav } from 'models';

import type { NavigationItem, NavigationMenu } from 'components/Layout/NavigationMenu';
import type { ReactNode } from 'react';

export type NavigationRouteKey = keyof typeof Routes;
type RouteOverrides = Partial<Record<NavigationRouteKey, string>>;
type NavItemDisplayOverride = Pick<Partial<NavigationItem>, 'name' | 'icon'>;
type NavItemDisplayOverrides = Partial<Record<NavigationRouteKey, NavItemDisplayOverride>>;

interface NavigationContextValue {
	topNav: NavigationMenu;
	routeOverrides: RouteOverrides;
	navItemDisplayOverrides: NavItemDisplayOverrides;
	getRoutePath: (routeKey: NavigationRouteKey) => string;
	setRoutePath: (routeKey: NavigationRouteKey, newPath: string) => void;
	resetRoutePath: (routeKey: NavigationRouteKey) => void;
	resetAllRoutePaths: () => void;
	setNavItemDisplay: (routeKey: NavigationRouteKey, updates: NavItemDisplayOverride) => void;
	resetNavItemDisplay: (routeKey: NavigationRouteKey) => void;
	resetAllNavItemDisplays: () => void;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

// Walks a navigation item and its children, replacing any href/text/icon that has an override.
// This lets callers update a route in one place and have the sidebar links follow it.
function applyNavigationOverridesToItem(
	item: NavigationItem,
	routeMap: Map<string, string>,
	displayMap: Map<string, NavItemDisplayOverride>
): NavigationItem {
	const nextHref = routeMap.get(item.href) ?? item.href;
	const displayOverride = displayMap.get(item.href) ?? {};

	return {
		...item,
		...displayOverride,
		href: nextHref,
		// Navigation items can be nested, so children need the same override pass.
		children: item.children?.map((child) => applyNavigationOverridesToItem(child, routeMap, displayMap)),
	};
}

// Provides the app's navigation menu plus a small override API for dynamic routes.
// Routes remains the source of truth; routeOverrides only stores temporary changes,
// such as keeping the Dashboard/MerlinAI link pointed at a resumable chat URL.
export function NavigationProvider({ children }: { children: ReactNode }) {
	const [routeOverrides, setRouteOverrides] = useState<RouteOverrides>({});
	const [navItemDisplayOverrides, setNavItemDisplayOverrides] = useState<NavItemDisplayOverrides>({});

	// Map every default route path to itself so the override map can be built from
	// original hrefs used in topNav to the currently resolved hrefs shown to users.
	const defaultRouteMap = useMemo(() => new Map(Object.values(Routes).map((routePath) => [routePath, routePath])), []);

	// Effective route table: default route paths plus any active route overrides.
	const resolvedRoutes = useMemo(
		() =>
			(Object.keys(Routes) as NavigationRouteKey[]).reduce((acc, routeKey) => {
				acc[routeKey] = routeOverrides[routeKey] ?? Routes[routeKey];
				return acc;
			}, {} as Record<NavigationRouteKey, string>),
		[routeOverrides]
	);

	// Build the navigation menu consumed by the header/sidebar.
	// The map is keyed by the original route path because topNav items store hrefs,
	// not route keys. Any matching href is replaced with the resolved route path.
	const topNavigation = useMemo<NavigationMenu>(() => {
		const routeMap = new Map(defaultRouteMap);
		const displayMap = new Map<string, NavItemDisplayOverride>();

		(Object.keys(routeOverrides) as NavigationRouteKey[]).forEach((routeKey) => {
			routeMap.set(Routes[routeKey], resolvedRoutes[routeKey]);
		});

		(Object.keys(navItemDisplayOverrides) as NavigationRouteKey[]).forEach((routeKey) => {
			const override = navItemDisplayOverrides[routeKey];

			if (override) {
				displayMap.set(Routes[routeKey], override);
			}
		});

		return {
			...topNav,
			items: topNav.items.map((item) => applyNavigationOverridesToItem(item, routeMap, displayMap)),
		};
	}, [defaultRouteMap, resolvedRoutes, routeOverrides, navItemDisplayOverrides]);

	const value = useMemo<NavigationContextValue>(
		() => ({
			topNav: topNavigation,
			routeOverrides,
			navItemDisplayOverrides,
			getRoutePath: (routeKey) => resolvedRoutes[routeKey],
			setRoutePath: (routeKey, newPath) => {
				setRouteOverrides((current) => {
					if (current[routeKey] === newPath) return current;

					return { ...current, [routeKey]: newPath };
				});
			},
			// Removes one override and falls back to the default value from Routes.
			resetRoutePath: (routeKey) => {
				setRouteOverrides((current) => {
					if (!(routeKey in current)) return current;

					const next = { ...current };
					delete next[routeKey];
					return next;
				});
			},
			// Clears every override, restoring the full navigation menu to default routes.
			resetAllRoutePaths: () => setRouteOverrides({}),
			setNavItemDisplay: (routeKey, updates) => {
				setNavItemDisplayOverrides((current) => {
					const nextOverride = { ...current[routeKey], ...updates };
					const currentOverride = current[routeKey];

					if (currentOverride?.name === nextOverride.name && currentOverride?.icon === nextOverride.icon) {
						return current;
					}

					return {
						...current,
						[routeKey]: nextOverride,
					};
				});
			},
			// Removes one display override and falls back to the default name/icon from topNav.
			resetNavItemDisplay: (routeKey) => {
				setNavItemDisplayOverrides((current) => {
					if (!(routeKey in current)) return current;

					const next = { ...current };
					delete next[routeKey];
					return next;
				});
			},
			// Clears every display override, restoring the full navigation menu to default names/icons.
			resetAllNavItemDisplays: () => setNavItemDisplayOverrides({}),
		}),
		[resolvedRoutes, routeOverrides, topNavigation, navItemDisplayOverrides]
	);

	return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigation() {
	const context = useContext(NavigationContext);

	if (!context) {
		throw new Error('useNavigation must be used within a NavigationProvider');
	}

	return context;
}

export default NavigationProvider;
